import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";

import * as api from "../api";
import { customFormKeys, useUpdateCustomForm } from "./index";
import UndoDeleteToast from "../Components/UndoDeleteToast";
import type { AutosaveState } from "../Components/AutosaveIndicator";
import type { CustomForm, CustomFormFieldType } from "../types";
import type { CustomFormFieldPayload } from "../models/payloads";

const CONTENT_DEBOUNCE_MS = 800;
const UNDO_TOAST_DURATION_MS = 8000;
const SCROLL_BEFORE_SELECT_MS = 250;

/** `clientKey` is a local render-stable id; `id`/`uuid` arrive when create resolves. Array order = display order. */
export interface EditorField extends CustomFormFieldPayload {
  clientKey: string;
  id?: number;
  uuid?: string;
}

let clientKeyCounter = 0;
const nextClientKey = () => {
  clientKeyCounter += 1;
  return `field-${clientKeyCounter}`;
};

function fieldsFromForm(form: CustomForm | undefined): EditorField[] {
  return (form?.customFormFields ?? []).map((f) => ({
    clientKey: `server-${f.id}`,
    id: f.id,
    uuid: f.uuid,
    fieldType: f.fieldType,
    label: f.label,
    helpText: f.helpText,
    required: f.required,
    options: f.options ?? {},
  }));
}

function newFieldOfType(type: CustomFormFieldType): CustomFormFieldPayload {
  return {
    fieldType: type,
    label: "Untitled question",
    helpText: null,
    required: false,
    options: type === "single_select" || type === "multi_select" ? { choices: [] } : {},
  };
}

function contentPayload(field: EditorField): CustomFormFieldPayload {
  const { clientKey, id, uuid, ...content } = field;
  return content;
}

interface Params {
  form: CustomForm | undefined;
  id: string | undefined;
}

/**
 * Optimistic local state; all server writes serialize through one promise queue
 * so a reorder can't race a delete. Structural ops fire instantly, content edits debounce.
 */
export default function useCustomFormEditorState({ form, id }: Params) {
  const { mutate: updateForm } = useUpdateCustomForm(id ?? "");
  const toast = useToast();
  const queryClient = useQueryClient();

  const [fields, setFields] = useState<EditorField[]>([]);
  const [instructions, setInstructions] = useState<string>("");
  const [responseScreenHeader, setResponseScreenHeader] = useState<string>("");
  const [responseScreenText, setResponseScreenText] = useState<string>("");
  const [showSubmitterInfo, setShowSubmitterInfo] = useState<boolean>(false);
  const [autosave, setAutosave] = useState<AutosaveState>({ kind: "idle" });
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const fieldsRef = useRef<EditorField[]>([]);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  // Queued/debounced saves outlive the builder on purpose; this only gates their UI feedback.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // clientKey -> server id; queued ops read ids at execute time, not enqueue time.
  const idByKeyRef = useRef<Map<string, number>>(new Map());

  const seededForFormRef = useRef<number | string | null>(null);
  const needsResyncRef = useRef(false);

  useEffect(() => {
    if (!form) return;
    if (seededForFormRef.current === form.id && !needsResyncRef.current) return;
    seededForFormRef.current = form.id;
    needsResyncRef.current = false;
    const seeded = fieldsFromForm(form);
    idByKeyRef.current = new Map(seeded.map((f) => [f.clientKey, f.id as number]));

    setFields(seeded);
    setInstructions(form.description ?? "");
    setResponseScreenHeader(form.responseScreenHeader ?? "");
    setResponseScreenText(form.responseScreenText ?? "");
    setShowSubmitterInfo(form.showSubmitterInfo ?? false);
  }, [form]);

  // ---- Serialized server ops ------------------------------------------------

  const opQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingOpsRef = useRef(0);
  const reorderQueuedRef = useRef(false);

  const runOp = useCallback(
    (op: () => Promise<CustomForm | null>) => {
      if (!id) return;
      pendingOpsRef.current += 1;
      setAutosave({ kind: "saving" });
      opQueueRef.current = opQueueRef.current
        .then(op)
        .then((serverForm) => {
          if (serverForm) queryClient.setQueryData(customFormKeys.detail(id), serverForm);
          pendingOpsRef.current -= 1;
          if (pendingOpsRef.current === 0) setAutosave({ kind: "saved", at: new Date() });
        })
        .catch(() => {
          pendingOpsRef.current -= 1;
          setAutosave({ kind: "error" });
          if (mountedRef.current) toast({ title: "Save failed", status: "error" });
          // Local state may have diverged; re-seed from the server.
          needsResyncRef.current = true;
          queryClient.invalidateQueries({ queryKey: customFormKeys.detail(id) });
        });
    },
    [id, queryClient, toast],
  );

  const enqueueCreate = useCallback(
    (row: EditorField) => {
      runOp(async () => {
        const serverForm = await api.createCustomFormField(id as string, contentPayload(row));
        const knownIds = new Set(idByKeyRef.current.values());
        const created = (serverForm.customFormFields ?? []).find((f) => !knownIds.has(f.id));
        if (created) {
          idByKeyRef.current.set(row.clientKey, created.id);
          setFields((prev) =>
            prev.map((f) => (f.clientKey === row.clientKey ? { ...f, id: created.id, uuid: created.uuid } : f)),
          );
        }
        return serverForm;
      });
    },
    [runOp, id],
  );

  // Coalesced: one queued reorder reads the freshest order when it executes.
  const enqueueReorder = useCallback(() => {
    if (reorderQueuedRef.current) return;
    reorderQueuedRef.current = true;
    runOp(async () => {
      reorderQueuedRef.current = false;
      const orderedIds = fieldsRef.current
        .map((f) => f.id ?? idByKeyRef.current.get(f.clientKey))
        .filter((fieldId): fieldId is number => fieldId != null);
      return api.reorderCustomFormFields(id as string, orderedIds);
    });
  }, [runOp, id]);

  // ---- Field content edits (debounced per field) ----------------------------

  const contentTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const setField = useCallback(
    (index: number, patch: Partial<CustomFormFieldPayload>) => {
      const clientKey = fieldsRef.current[index]?.clientKey;
      setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
      if (!clientKey) return;

      const timers = contentTimersRef.current;
      clearTimeout(timers.get(clientKey));
      setAutosave({ kind: "saving" });
      timers.set(
        clientKey,
        setTimeout(() => {
          timers.delete(clientKey);
          runOp(async () => {
            const row = fieldsRef.current.find((f) => f.clientKey === clientKey);
            if (!row) return null; // deleted before the debounce fired
            const fieldId = row.id ?? idByKeyRef.current.get(clientKey);
            if (fieldId == null) return null; // create failed; error path resyncs
            return api.updateCustomFormField(id as string, fieldId, contentPayload(row));
          });
        }, CONTENT_DEBOUNCE_MS),
      );
    },
    [runOp, id],
  );

  // ---- Structural ops (instant, no debounce) ---------------------------------

  const addFieldOfType = useCallback(
    (type: CustomFormFieldType) => {
      const row: EditorField = { clientKey: nextClientKey(), ...newFieldOfType(type) };
      const index = fieldsRef.current.length;
      setFields((prev) => [...prev, row]);
      enqueueCreate(row);

      // Scroll the new card into view, then open its inspector.
      requestAnimationFrame(() => {
        const card = document.querySelector(`[data-field-key="${row.clientKey}"]`);
        if (card?.scrollIntoView) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => setSelectedIndex(index), SCROLL_BEFORE_SELECT_MS);
        } else {
          setSelectedIndex(index);
        }
      });
    },
    [enqueueCreate],
  );

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setFields((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
      setSelectedIndex((prev) => {
        if (prev == null) return prev;
        if (prev === fromIndex) return toIndex;
        if (fromIndex < prev && toIndex >= prev) return prev - 1;
        if (fromIndex > prev && toIndex <= prev) return prev + 1;
        return prev;
      });
      enqueueReorder();
    },
    [enqueueReorder],
  );

  const duplicate = useCallback(
    (index: number) => {
      const source = fieldsRef.current[index];
      if (!source) return;
      const clone: EditorField = {
        ...contentPayload(source),
        clientKey: nextClientKey(),
        label: `${source.label} (copy)`,
      };
      setFields((prev) => [...prev.slice(0, index + 1), clone, ...prev.slice(index + 1)]);
      enqueueCreate(clone);
      enqueueReorder(); // create appends; move the clone next to its source
    },
    [enqueueCreate, enqueueReorder],
  );

  const removeField = useCallback(
    (index: number) => {
      const target = fieldsRef.current[index];
      if (!target) return;

      setFields((prev) => prev.filter((_, i) => i !== index));
      setSelectedIndex((prev) => (prev === index ? null : prev));
      runOp(async () => {
        const fieldId = target.id ?? idByKeyRef.current.get(target.clientKey);
        if (fieldId == null) return null; // never persisted
        return api.deleteCustomFormField(id as string, fieldId);
      });

      toast({
        duration: UNDO_TOAST_DURATION_MS,
        isClosable: true,
        render: ({ onClose }) =>
          UndoDeleteToast({
            label: "Field deleted",
            onClose,
            onUndo: () => {
              // Original was archived server-side; undo re-creates and reorders back into place.
              const restored: EditorField = { ...contentPayload(target), clientKey: nextClientKey() };
              setFields((prev) => {
                const next = [...prev];
                next.splice(Math.min(index, next.length), 0, restored);
                return next;
              });
              enqueueCreate(restored);
              enqueueReorder();
            },
          }),
      });
    },
    [runOp, enqueueCreate, enqueueReorder, id, toast],
  );

  // ---- Form-level copy (name, instructions, response screen) ----------------

  const persist = useCallback(
    (payload: {
      name?: string;
      description?: string | null;
      responseScreenHeader?: string | null;
      responseScreenText?: string | null;
      showSubmitterInfo?: boolean;
    }) => {
      if (!id) return;
      setAutosave({ kind: "saving" });
      updateForm(
        { customForm: payload },
        {
          onSuccess: () => setAutosave({ kind: "saved", at: new Date() }),
          onError: () => {
            setAutosave({ kind: "error" });
            if (mountedRef.current) toast({ title: "Save failed", status: "error" });
          },
        },
      );
    },
    [id, updateForm, toast],
  );

  const setInstructionsAndPersist = useCallback(
    (value: string) => {
      setInstructions(value);
      persist({ description: value || null });
    },
    [persist],
  );

  const persistName = useCallback((name: string) => persist({ name }), [persist]);

  const setResponseScreenHeaderAndPersist = useCallback(
    (value: string) => {
      setResponseScreenHeader(value);
      persist({ responseScreenHeader: value || null });
    },
    [persist],
  );

  const setShowSubmitterInfoAndPersist = useCallback(
    (value: boolean) => {
      setShowSubmitterInfo(value);
      persist({ showSubmitterInfo: value });
    },
    [persist],
  );

  const setResponseScreenTextAndPersist = useCallback(
    (value: string) => {
      setResponseScreenText(value);
      persist({ responseScreenText: value || null });
    },
    [persist],
  );

  return {
    fields,
    instructions,
    autosave,
    selectedIndex,
    setSelectedIndex,
    setField,
    reorder,
    duplicate,
    removeField,
    addFieldOfType,
    setInstructions: setInstructionsAndPersist,
    persistName,
    responseScreenHeader,
    responseScreenText,
    setResponseScreenHeader: setResponseScreenHeaderAndPersist,
    setResponseScreenText: setResponseScreenTextAndPersist,
    showSubmitterInfo,
    setShowSubmitterInfo: setShowSubmitterInfoAndPersist,
  };
}
